<template>
    <div class="enquiry-form-form">
        <div
            v-if="$slots['title'] || $slots['description']"
            class="form-header"
        >
            <div
                v-if="$slots['title']"
                class="form-title"
            >
                <slot name="title"></slot>
            </div>

            <div
                v-if="$slots['description']"
                class="form-description"
            >
                <slot name="description"></slot>
            </div>
        </div>

        <form
            class="form"
            name="register"
            @submit.prevent="submit"
        >
            <fieldset
                :disabled="disabled"
                class="fieldset"
            >
                <div v-if="!hideInputsIfLogged || !isLogged">
                    <FormRow>
                        <TextField
                            id="form-first-name"
                            v-model="names"
                            :error="
                                errorsWithFeValidation.last(
                                    'customer.firstName'
                                )
                            "
                            :label="t('enquiry.name')"
                            :placeholder="t('enquiry.name')"
                            lazy
                            required
                        />
                    </FormRow>

                    <FormRow>
                        <TextField
                            id="form-email"
                            v-model="email"
                            :error="
                                errorsWithFeValidation.last('customer.email')
                            "
                            :label="t('enquiry.email')"
                            :placeholder="t('enquiry.email')"
                            lazy
                            required
                        />
                    </FormRow>
                </div>

                <slot name="contact"></slot>

                <div v-if="!hideInputsIfLogged || !isLogged">
                    <FormRow>
                        <ThCheckbox
                            v-model="emailConsent"
                            :error="
                                errorsWithFeValidation.last(
                                    'customer.emailConsent'
                                )
                            "
                        >
                            {{ t('enquiry.email_consent_label') }}
                        </ThCheckbox>
                    </FormRow>
                </div>

                <FormActionErrors :errors="errorsWithFeValidation" />

                <ThButton
                    v-qa="'submit'"
                    :full-width="true"
                    :loading="disabled"
                    class="th-button"
                    submit
                    type="solid"
                >
                    <slot name="submit">
                        {{ t('enquiry.submit') }}
                    </slot>
                </ThButton>

                <div class="gsi-separator">
                    <span>or</span>
                </div>

                <div class="gsi-wrapper">
                    <div
                        ref="googleSignInButtonEl"
                        class="google-sign-in-button"
                    ></div>
                </div>
            </fieldset>
        </form>

        <ConsentNotice class="consent-notice" />
    </div>
</template>

<script lang="ts" setup>
import { toRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'

import type { ErrorCollectionWithTraces } from '@/helpers/errors'
import { useAuth } from '@/modules/auth'
import { useCustomerRegistrationInputStore } from '@/composables/useCustomerRegistrationInput'
import { useEmailAndNamesFieldsError } from './useEmailAndNamesFieldsError'
import { useGoogleSignIn } from '@/composables/auth/useGoogleSignIn'

import FormRow from '@/components/ui/form/FormRow.vue'
import TextField from '@/components/ui/inputs/TextField.vue'
import ThCheckbox from '@/components/ui/inputs/ThCheckbox.vue'
import ThButton from '@/components/ui/buttons/ThButton.vue'
import FormActionErrors from '@/components/ui/form/error/FormActionErrors.vue'
import ConsentNotice from './ConsentNotice.vue'

interface Props {
    errors: ErrorCollectionWithTraces
    disabled?: boolean
    hideInputsIfLogged?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    disabled: false,
    isContactUsForm: false,
})

const emit = defineEmits<{
    submit: []
}>()

const { t } = useI18n()

const { email, emailConsent, names, validatedEmail } = storeToRefs(
    useCustomerRegistrationInputStore()
)

const { isLogged } = useAuth()

const emitSubmit = () => emit('submit')

const { submit, errorsWithFeValidation } = useEmailAndNamesFieldsError(
    isLogged,
    toRef(() => props.errors),
    names,
    email,
    validatedEmail,
    emitSubmit
)

const { googleSignInButtonEl } = useGoogleSignIn(() => new Promise(emitSubmit))
</script>

<style lang="scss" scoped>
.enquiry-form-form {
    min-height: calc(100vh - 100px);
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-evenly;

    .form-header {
        width: 100%;
        max-width: 500px;
        margin: 0 auto 40px;
        padding: 0 20px;

        .form-title {
            font-family: $parcel-font-stack;
            font-size: 2rem;
            line-height: 1em;
            color: $blue;
            text-align: center;
            margin: 0 0 30px;
        }

        .form-description {
            text-align: center;
            line-height: 1.4em;
        }
    }

    .form {
        padding: 0 20px;
        margin: 0 auto;
        width: 100%;
        max-width: 380px;

        .fieldset:disabled {
            opacity: 0.5;
        }

        .section-title {
            color: $slate;
            font-size: 1rem;
            line-height: 1.5rem;
            margin: 40px 0 10px;
        }

        .gsi-wrapper {
            margin-top: 20px;
            display: flex;
            justify-content: center;
        }

        .gsi-separator {
            text-align: center;
            display: flex;
            align-items: center;
            max-width: 200px;
            margin: 30px auto 20px;
            text-transform: uppercase;
            font-family: $effra-font-stack;
            letter-spacing: 0.05em;
            font-size: 0.875rem;

            &:before,
            &:after {
                content: '';
                display: inline-block;
                width: 50%;
                height: 1px;
                background-color: $grey-200;
            }

            span {
                display: block;
                margin: 0 10px;
            }
        }
    }

    .consent-notice {
        padding: 0 20px;
        margin-top: 20px;
        width: 100%;
        max-width: 380px;
    }
}
</style>
